import dynamic from 'next/dynamic';
import TagContainer from '@enums/tagContainer';
import AddToCartModal from '@components/molecules/products/add-to-cart-modal/add-to-cart-modal';
import { useAnalytics } from '@context/analytics/analytics.context';
import { CartContext } from '@context/cart.context';
import { ProductPageQueryKeys } from '@enums/react-query-keys';
import usePdpLayout from '@hooks/product/use-pdp-layout';
import { useQuery } from '@tanstack/react-query';
import React, { Dispatch, SetStateAction, useState } from 'react';
import clsx from 'clsx';
import { useContextSelector } from 'use-context-selector';
import AuthenticationGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import { useTranslation } from 'next-i18next';
import Icon from '@components/atoms/icon/icon';
import Button from '@components/atoms/button/button';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import ChatApiService from '@services/chat-service';
import logger from '@helpers/utils/logger/client';
import useProductQuery from '@hooks/product/use-product-query';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import useUser from '@hooks/user/use-user';
import useFeatureFlag from '@hooks/use-feature-flags';
import useWindowSize from '@hooks/use-window-size';
import { useCtaProductContext } from '../context';
import styles from '../CTA-product.module.scss';
import CartButton from './cart-button';
import OfferButton from './offer-button';
import VacationNotificationButton from './vacation-notification-button';

const Chat = dynamic(() => import('@components/organisms/chat/chat'), {
  ssr: false,
});

export interface CTAButtonsBlockProps {
  customClass?: string;
  openFromAuthModal: boolean;
  setOpenNegotiationFromAuthModal: Dispatch<SetStateAction<boolean>>;
}

const CTAButtonsBlock: React.FC<CTAButtonsBlockProps> = ({
  customClass,
  openFromAuthModal,
  setOpenNegotiationFromAuthModal,
}) => {
  const { trackOnClickNegotiation } = useCtaProductContext();

  const { t } = useTranslation();
  const { sendEvent } = useAnalytics();
  const { sendAnalyticEvent: sendChatAnalyticEvents } = useAnalyticEvents('chat');
  const { isRecirculation } = usePdpLayout();
  const { isHeroPdpStyles } = useHeroPdpStyles();
  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);
  const { user } = useUser();
  const { product, userTransactionRole, isProductBuyable, isSellerOnVacation } = useProductQuery();
  const { isWiderThanMd } = useWindowSize();
  const { isSemPDP } = useFeatureFlag();

  const [triggerAddToCart, setTriggerAddToCart] = useState<boolean>(false);
  const [isChatModalOpened, setIsChatModalOpened] = useState<boolean>(false);
  const [holidaysEndAlertError, setHolidaysEndAlertError] = useState<string>(null);
  const { data: isChatEligible } = useQuery<boolean>({
    queryKey: [ProductPageQueryKeys.CHAT_ELIGIBLE, product.id, user?.id],
    queryFn: () =>
      ChatApiService.getEligibility({
        sellerId: product.seller.id,
        buyerId: user.id,
        sellerSegment: user.segment,
        productId: product.id,
      }),
    initialData: false,
    enabled: !!user,
  });

  const showChatCTA = (): boolean => {
    const userHasChatEligible = user && isChatEligible;
    if (isSemPDP) {
      return userHasChatEligible;
    }
    const notSoldOnHeroPDP = isHeroPdpStyles && !product.sold;
    return userHasChatEligible && !notSoldOnHeroPDP;
  };
  const addToCart = async (): Promise<void> => {
    try {
      await addToShoppingCart(product, true);
    } catch (e) {
      logger.info('Error adding to cart - CTA-buttons-block');
    }
  };

  const closeAddToCartDialog = (): void => {
    sendEvent({
      type: 'event',
      container: TagContainer.Analytics,
      payload: {
        event: 'popinClose',
        popinName: 'AddToCart',
      },
    });
  };

  const trackChatClick = (origin: 'report' | 'message_sent'): void => {
    const chatPart = userTransactionRole === 'seller' ? 'mmao_seller' : 'product_buyer';
    sendEvent({
      type: 'event',
      payload: {
        event: 'bs_chat',
        chat_action_name: origin || '',
        chat_context: chatPart,
      },
    });
    sendChatAnalyticEvents('pdp_start_chat');
    sendChatAnalyticEvents('bs_chat_view');
  };

  return (
    <>
      {/* Condition to not display an empty container */}
      {(isProductBuyable || isSellerOnVacation || showChatCTA()) && (
        <div
          className={clsx(styles.CTAButtonsBlock, { [styles['CTAButtonsBlock--semPDP']]: isSemPDP }, customClass ?? '')}
        >
          {/* Add to cart button */}
          {isProductBuyable && (
            <AuthenticationGuard
              className={clsx(
                styles.CTAButtonsBlock__authGuardWrapper,
                !isHeroPdpStyles && styles['CTAButtonsBlock__authGuardWrapper--addToCart'],
              )}
              onAuth={() => setTriggerAddToCart(!triggerAddToCart)}
            >
              <CartButton triggerAddToCart={triggerAddToCart} />
            </AuthenticationGuard>
          )}
          {/* Alert button */}
          {isSellerOnVacation && (
            <AuthenticationGuard
              className={clsx(
                styles.CTAButtonsBlock__authGuardWrapper,
                styles['CTAButtonsBlock__authGuardWrapper--alert'],
              )}
            >
              <VacationNotificationButton
                holidaysEndAlertError={holidaysEndAlertError}
                setHolidaysEndAlertError={setHolidaysEndAlertError}
              />
            </AuthenticationGuard>
          )}
          {/* MMAO button */}
          <>
            {isProductBuyable && !isSellerOnVacation && (
              <OfferButton
                openFromAuthModal={openFromAuthModal}
                onClick={trackOnClickNegotiation}
                setOpenNegotiationFromAuthModal={setOpenNegotiationFromAuthModal}
              />
            )}
            {/* Chat button */}
            {showChatCTA() && (
              <Button
                variant="secondary"
                size={isRecirculation ? 'medium' : 'large'}
                fullWidth
                disabled={!(user && isChatEligible)}
                data-cy="pdp_chat_btn"
                data-vc-dd-action-name="pdp_chat_button"
                onClick={() => {
                  trackChatClick('report');
                  setIsChatModalOpened(true);
                }}
                className={clsx(
                  styles.CTAButtonsBlock__button,
                  styles['CTAButtonsBlock__button--chat'],
                  { 'vc-btn--large--md-up': !isRecirculation }, // Make button full width if it's the only button displayed
                  { [styles['CTAButtonsBlock__button--fullWidth']]: !isProductBuyable && !isSellerOnVacation },
                  { [styles['CTAButtonsBlock__button--semPDPIteration']]: isSemPDP },
                )}
              >
                <Icon
                  name={isSemPDP ? 'chat-default' : 'chat'}
                  className={styles.CTAButtonsBlock__buttonIcon}
                  {...(isSemPDP && isWiderThanMd
                    ? { ariaLabel: t('PRODUCT.DETAILS.CTA.CHAT') }
                    : { 'aria-hidden': true })}
                />
                {isSemPDP && isWiderThanMd ? null : (
                  <span className={styles['CTAButtonsBlock__buttonText--chat']}>{t('PRODUCT.DETAILS.CTA.CHAT')}</span>
                )}
              </Button>
            )}
          </>
        </div>
      )}
      {/* Seller on vacation - alert creation error */}
      {holidaysEndAlertError && (
        <div className={styles['CTAButtonsBlock__button--alertError']}>{holidaysEndAlertError}</div>
      )}

      {isChatModalOpened && user && (
        <Chat
          addToCart={addToCart}
          product={product}
          closeHandler={() => setIsChatModalOpened(false)}
        />
      )}
      <AddToCartModal onCloseDialog={closeAddToCartDialog} />
    </>
  );
};

export default CTAButtonsBlock;
